import type { Question } from '@/models/question';
import type { PositionAnswerValue } from '@/models/position';
import type { Topic } from '@/models/topic';

const answerScore: Record<Exclude<PositionAnswerValue, 'skip'>, number> = {
  disagree: -2,
  partiallyDisagree: -1,
  neutral: 0,
  partiallyAgree: 1,
  agree: 2,
};

export function getAnswerLabel(answer: PositionAnswerValue): string {
  switch (answer) {
    case 'disagree':
      return 'Discordo';
    case 'partiallyDisagree':
      return 'Discordo em parte';
    case 'neutral':
      return 'Não tenho posição';
    case 'partiallyAgree':
      return 'Concordo em parte';
    case 'agree':
      return 'Concordo';
    case 'skip':
      return 'Pulada';
    default:
      return 'Não tenho posição';
  }
}

export function summarizeTopicPosition(
  topic: Topic,
  questions: Question[],
  answers: Record<string, PositionAnswerValue>,
): string {
  const topicQuestions = questions.filter((question) => question.topicId === topic.id);
  const answeredValues = topicQuestions
    .map((question) => answers[question.id])
    .filter((answer): answer is Exclude<PositionAnswerValue, 'skip'> =>
      Boolean(answer && answer !== 'skip'),
    );

  if (answeredValues.length === 0) {
    return topic.positionStatements.neutral;
  }

  const score = answeredValues.reduce((sum, value) => sum + answerScore[value], 0);
  const average = score / answeredValues.length;

  if (average >= 1.5) {
    return topic.positionStatements.agree;
  }

  if (average >= 0.5) {
    return topic.positionStatements.partiallyAgree;
  }

  if (average > -0.5) {
    return topic.positionStatements.neutral;
  }

  if (average > -1.5) {
    return topic.positionStatements.partiallyDisagree;
  }

  return topic.positionStatements.disagree;
}

export function getTopicLatestAnswer(
  topicId: string,
  questions: Question[],
  answers: Record<string, PositionAnswerValue>,
): PositionAnswerValue | null {
  for (let index = questions.length - 1; index >= 0; index -= 1) {
    const question = questions[index];

    if (!question) {
      continue;
    }

    if (question.topicId !== topicId) {
      continue;
    }

    const answer = answers[question.id];

    if (answer && answer !== 'skip') {
      return answer;
    }
  }

  return null;
}
